import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Option, OptionGroup} from '../../../shared/models/option';
import {ClickService} from '../../../core/services/click.service';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {Subscription} from 'rxjs';
import {RouterLink} from '@angular/router';
import {NgClass} from '@angular/common';

export type PopoverMenuHoverColors = '' | 'indigo' | 'indigo-dark' | 'green';

/**
 * Stateless menu overlay - parent manages open/closed state.
 * For simple button+menu, use <motn-menu-button> instead.
 */
@Component({
  selector: 'motn-popover-menu',
  templateUrl: './popover-menu.component.html',
  styleUrls: ['./popover-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    RouterLink,
    NgClass,
  ],
})
export class PopoverMenuComponent implements OnDestroy, AfterViewInit, OnChanges {
  @Input() hoverColor: PopoverMenuHoverColors;
  @Input() menuTitle: string;
  @Input() optionGroup: OptionGroup;
  @Input() viewportMargin: number = 20;
  @Input() overlayPush = true;
  @Input() overlayLockPosition = true;

  overlayOpen = true;

  @Input() overlayPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'bottom',
  },{
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'top',
  }];

  @Output() clickedOption$: EventEmitter<Option> = new EventEmitter<Option>();
  @Output() clickedOutsideMenu$: EventEmitter<null> = new EventEmitter<null>();
  @Output() clickedAnywhere$: EventEmitter<Option | null> = new EventEmitter<Option | null>();

  clickSub: Subscription;

  @ViewChild('menuOverlay', {read: ElementRef}) private menuEl: ElementRef<HTMLElement>;
  @ViewChild(CdkConnectedOverlay) private connectedOverlay: CdkConnectedOverlay;

  constructor(
    private eRef: ElementRef,
    private clickService: ClickService,
  ) {
  }

  get options(): Option[] {
    return this.optionGroup?.options || [];
  }

  ngAfterViewInit() {
    const preselected = this.options.find(o => o.selected);

    if (preselected) {
      const selector: string = `[data-id="${preselected.id}"]`;
      const parentEl: HTMLElement = this.menuEl.nativeElement;
      const thisEl: HTMLAnchorElement = parentEl.querySelector(selector);

      // This places the vertical center of the selected menu option
      // either at the very top of the scrollable area (i.e. making
      // the top half of the option cut off) or lower (i.e. less than
      // half of the option is cut off at the top or the option appears
      // entirely within the current viewable area of the scrollable
      // area).
      thisEl.scrollIntoView({
        block: 'center',
      });

      setTimeout(() => {
        const selectedOptionOffsetTop = thisEl.offsetTop;
        const selectedOptionHeight = thisEl.scrollHeight;
        const visibleMenuHeight = parentEl.clientHeight;
        const totalMenuHeight = parentEl.scrollHeight;

        let scrollByPx = (totalMenuHeight - selectedOptionOffsetTop) - (visibleMenuHeight / 2) - (selectedOptionHeight / 2);
        if (scrollByPx > visibleMenuHeight / 2) {
          scrollByPx = visibleMenuHeight / 2;
        }

        if (scrollByPx > 0) {
          parentEl.scrollBy({
            top: scrollByPx * -1,
          });
        }
      }, 0);
    }

    this.clickSub = this.clickService.click$.subscribe(
      ($el) => {
        if (!this.eRef.nativeElement.contains($el)) {
          this.clickedOutsideMenu$.emit(null);
          this.clickedAnywhere$.emit(null);
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // When optionGroup changes (loading -> actual options), update position
    if (changes['optionGroup'] && this.connectedOverlay?.overlayRef) {
      this.connectedOverlay.overlayRef.updatePosition();
    }
  }

  ngOnDestroy() {
    this.clickSub?.unsubscribe();
  }

  onClick(option: Option) {
    if (option.callback) {
      option.callback();
    }

    // this.optionGroup.changeSelected(option.id);

    this.clickedOption$.emit(option);
    this.clickedAnywhere$.emit(option);
  }
}
